import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ValidatorComponent } from '@feature/survey-management/components/validator/validator.component';
import { TranslateModule } from '@ngx-translate/core';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { Matrix } from '@shared/models/question-types/matrix';
import { SafeHtmlPipe } from '../../../../../../shared/pipes/safe-html.pipe';
import { ToValidIdPipe } from '../../../../../../shared/pipes/to-valid-id.pipe';
import { MatrixColumnWidthUtil } from '../../../../../../shared/utils/matrix-column-width.util';
import { MatrixSingleSelectRowComponent } from './matrix-single-select-row/matrix-single-select-row.component';

@Component({
  selector: 'app-matrix-single-select',
  imports: [
    ReactiveFormsModule,
    MatrixSingleSelectRowComponent,
    TranslateModule,
    SafeHtmlPipe,
    ToValidIdPipe,
    ValidatorComponent,
  ],
  templateUrl: './matrix-single-select.component.html',
  styleUrl: './matrix-single-select.component.scss',
})
export class MatrixSingleSelectComponent implements AfterViewInit, OnInit, OnDestroy {
  private static readonly OVERFLOW_THRESHOLD_PX = 10;

  @Input() matrixQuestion!: Matrix;
  @Input() form?: FormGroup;

  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('scrollbarTrack') scrollbarTrack?: ElementRef<HTMLDivElement>;
  @ViewChild('stickyHeader') stickyHeader?: ElementRef<HTMLDivElement>;
  @ViewChild('stickyInner') stickyInner?: ElementRef<HTMLDivElement>;

  ValidatorType = ValidatorType;
  isMobileView: boolean;

  hasHorizontalOverflow = false;
  scrollbarThumbWidth = 100;
  scrollbarThumbPosition = 0;
  firstColumnWidth = 0;
  headerFirstRowHeight = 0;
  private headerHeight = 0;
  private tableWidth = 0;
  private isDragging = false;
  private dragStartX = 0;
  private scrollStartX = 0;
  private scrollParent?: HTMLElement | Window;
  private resizeObserver?: ResizeObserver;
  private readonly onLayoutScroll = () => {
    this.updateStickyPosition();
    this.handleScrollWithBlur();
  };
  private readonly onWindowResize = () => {
    this.updateScrollbarDimensions();
    this.updateStickyPosition();
  };

  get allRowsHaveRequiredError(): boolean {
    if (!this.form) return false;
    return (
      this.matrixQuestion.rows.length > 0 &&
      this.matrixQuestion.rows.every((row) => {
        const control = this.form!.get(row.id);
        return control?.hasError('required') && control.touched;
      })
    );
  }

  get isSliderPresentation(): boolean {
    return this.matrixQuestion.rows[0]?.presentation === 'SLIDER';
  }

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    this.isMobileView = window.innerWidth < 768;
  }

  ngOnInit(): void {
    window.addEventListener('resize', this.onResize.bind(this));
  }

  ngAfterViewInit(): void {
    if (this.isSliderPresentation || this.isMobileView) {
      return;
    }

    this.scrollParent = this.getScrollParent();

    if (this.scrollParent instanceof Window) {
      window.addEventListener('scroll', this.onLayoutScroll, { passive: true });
    } else {
      this.scrollParent?.addEventListener('scroll', this.onLayoutScroll, {
        passive: true,
      });
    }
    window.addEventListener('resize', this.onWindowResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollbarDimensions();
        this.updateStickyPosition();
      });

      const container = this.tableContainer?.nativeElement;
      if (container) {
        this.resizeObserver.observe(container);
      }

      const table = container?.querySelector('table');
      if (table) {
        this.resizeObserver.observe(table);
      }
    }

    this.checkOverflow();

    setTimeout(() => {
      this.setRemoveScrollbar();
      setTimeout(() => {
        this.updateScrollbarDimensions();
        this.updateStickyPosition();
        this.handleScrollWithBlur();
      }, 50);
    }, 100);

    this.elementRef.nativeElement.addEventListener(
      'recalculate-overflow',
      this.handleRecalculateOverflow.bind(this),
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
    window.removeEventListener('resize', this.onWindowResize);
    if (this.scrollParent instanceof Window) {
      window.removeEventListener('scroll', this.onLayoutScroll);
    } else {
      this.scrollParent?.removeEventListener('scroll', this.onLayoutScroll);
    }
    this.resizeObserver?.disconnect();
    this.elementRef.nativeElement.removeEventListener(
      'recalculate-overflow',
      this.handleRecalculateOverflow.bind(this),
    );
  }

  onResize(event: Event): void {
    this.isMobileView = (event.target as Window).innerWidth < 768;
    if (!this.isMobileView && !this.isSliderPresentation) {
      setTimeout(() => {
        this.updateScrollbarDimensions();
        this.onTableScroll();
        this.updateStickyPosition();
        this.handleScrollWithBlur();
      }, 100);
    }
  }

  handleRecalculateOverflow(): void {
    setTimeout(() => {
      this.setRemoveScrollbar();
      this.updateScrollbarDimensions();
      this.onTableScroll();
      this.updateStickyPosition();
      this.handleScrollWithBlur();
    }, 200);
  }

  checkOverflow(): void {
    setTimeout(() => {
      this.setRemoveScrollbar();
    }, 100);
  }

  private setRemoveScrollbar(): void {
    setTimeout(() => {
      const container = this.tableContainer?.nativeElement;

      if (!container) {
        return;
      }

      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const overflowAmount =
        scrollWidth - (clientWidth + MatrixSingleSelectComponent.OVERFLOW_THRESHOLD_PX);
      const hasOverflow = overflowAmount > 0;

      this.hasHorizontalOverflow = hasOverflow;

      if (hasOverflow) {
        this.updateScrollbarDimensions();
      }

      this.cdr.detectChanges();
    }, 100);
  }

  private updateScrollbarDimensions(): void {
    const container = this.tableContainer?.nativeElement;
    const track = this.scrollbarTrack?.nativeElement;

    if (!container) {
      return;
    }

    const firstColumnCell = container.querySelector('tbody tr th:first-child');
    if (firstColumnCell) {
      this.firstColumnWidth = firstColumnCell.getBoundingClientRect().width;
    }

    const headerFirstRow = container.querySelector('thead tr:first-child');
    if (headerFirstRow) {
      this.headerFirstRowHeight = headerFirstRow.getBoundingClientRect().height;
    }

    const thead = container.querySelector('thead');
    if (thead) {
      this.headerHeight = thead.getBoundingClientRect().height;
    }

    const table = container.querySelector('table');
    if (table) {
      this.tableWidth = table.getBoundingClientRect().width;
    }

    this.elementRef.nativeElement.style.setProperty(
      '--matrix-first-column-width',
      `${Math.round(this.firstColumnWidth)}px`,
    );
    this.elementRef.nativeElement.style.setProperty(
      '--matrix-header-height',
      `${Math.round(this.headerHeight)}px`,
    );
    this.elementRef.nativeElement.style.setProperty(
      '--matrix-table-width',
      `${Math.round(this.tableWidth || container.scrollWidth)}px`,
    );

    const effectiveTrackWidth = container.clientWidth - this.firstColumnWidth;
    const scrollableWidth = container.scrollWidth - this.firstColumnWidth;
    const visibleRatio = effectiveTrackWidth / scrollableWidth;

    if (track) {
      this.scrollbarThumbWidth = Math.max(40, track.clientWidth * visibleRatio);
    }

    this.syncStickyColumnWidths();
    this.updateStickyHorizontal();
  }

  onTableScroll(): void {
    this.handleScrollWithBlur();

    if (this.isDragging) {
      this.updateStickyHorizontal();
      return;
    }

    const container = this.tableContainer?.nativeElement;
    const track = this.scrollbarTrack?.nativeElement;

    if (!container || !track) {
      return;
    }

    const scrollRatio =
      container.scrollLeft / (container.scrollWidth - container.clientWidth);
    const maxThumbPosition = track.clientWidth - this.scrollbarThumbWidth;
    this.scrollbarThumbPosition = scrollRatio * maxThumbPosition;

    this.updateStickyHorizontal();
  }

  private handleScrollWithBlur(): void {
    const wrapper = this.elementRef.nativeElement.querySelector('.matrix-wrapper');
    const container = this.tableContainer?.nativeElement;
    const stickyHeader = this.stickyHeader?.nativeElement;

    if (!wrapper || !container) return;

    const hasHorizontalScroll = container.scrollLeft > 0;
    const hasVerticalStickyActive = stickyHeader?.classList.contains('is-active');

    if (hasHorizontalScroll || hasVerticalStickyActive) {
      wrapper.classList.add('is-scrolling');
    } else {
      wrapper.classList.remove('is-scrolling');
    }
  }

  onScrollbarThumbMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.scrollStartX = this.scrollbarThumbPosition;
  }

  onScrollbarTrackClick(event: MouseEvent): void {
    const track = this.scrollbarTrack?.nativeElement;
    const container = this.tableContainer?.nativeElement;

    if (!track || !container) {
      return;
    }

    if ((event.target as HTMLElement).classList.contains('custom-scrollbar-thumb')) {
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const clickPosition = event.clientX - trackRect.left;
    const maxThumbPosition = track.clientWidth - this.scrollbarThumbWidth;
    const newThumbPosition = Math.max(
      0,
      Math.min(clickPosition - this.scrollbarThumbWidth / 2, maxThumbPosition),
    );

    const scrollRatio = newThumbPosition / maxThumbPosition;
    container.scrollLeft =
      scrollRatio * (container.scrollWidth - container.clientWidth);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) {
      return;
    }

    const track = this.scrollbarTrack?.nativeElement;
    const container = this.tableContainer?.nativeElement;

    if (!track || !container) {
      return;
    }

    const deltaX = event.clientX - this.dragStartX;
    const maxThumbPosition = track.clientWidth - this.scrollbarThumbWidth;
    const newThumbPosition = Math.max(
      0,
      Math.min(this.scrollStartX + deltaX, maxThumbPosition),
    );

    this.scrollbarThumbPosition = newThumbPosition;

    const scrollRatio = newThumbPosition / maxThumbPosition;
    container.scrollLeft =
      scrollRatio * (container.scrollWidth - container.clientWidth);
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  private updateStickyHorizontal(): void {
    const container = this.tableContainer?.nativeElement;
    const stickyInner = this.stickyInner?.nativeElement;

    if (!container || !stickyInner) {
      return;
    }

    const baseLeft = -container.scrollLeft;
    stickyInner.style.transform = 'none';
    stickyInner.style.left = `${baseLeft}px`;
    stickyInner.style.width = `${this.tableWidth || container.scrollWidth}px`;
  }

  private updateStickyPosition(): void {
    const container = this.tableContainer?.nativeElement;
    const sticky = this.stickyHeader?.nativeElement;
    const scrollParent = this.scrollParent;

    if (!container || !sticky || !scrollParent) {
      return;
    }

    const table = container.querySelector('table');
    if (!table) {
      return;
    }

    const isWindow = scrollParent instanceof Window;
    const stickyOffset = 0;
    const layoutRectTop = isWindow
      ? 0
      : (scrollParent as HTMLElement).getBoundingClientRect().top;
    const tableRect = table.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const thead = table.querySelector('thead');
    const theadRect = thead?.getBoundingClientRect();
    const scrollTop = isWindow
      ? window.scrollY
      : (scrollParent as HTMLElement).scrollTop;
    const viewportHeight = isWindow
      ? window.innerHeight
      : (scrollParent as HTMLElement).clientHeight;
    const tableTop = tableRect.top - layoutRectTop + scrollTop;
    const tableBottom = tableTop + tableRect.height;
    const headerHeight = this.headerHeight || 0;
    const headerTop =
      theadRect?.top !== undefined
        ? theadRect.top - layoutRectTop + scrollTop
        : tableTop;

    const canScrollVertically = tableRect.height > viewportHeight;
    const shouldShow =
      canScrollVertically &&
      headerHeight > 0 &&
      scrollTop >= headerTop + stickyOffset &&
      scrollTop <= tableBottom - headerHeight;

    sticky.style.setProperty('--matrix-sticky-offset', `${stickyOffset}px`);
    sticky.style.top = `${layoutRectTop}px`;
    sticky.style.left = `${containerRect.left}px`;
    sticky.style.width = `${containerRect.width}px`;
    sticky.style.height = `${headerHeight + stickyOffset}px`;

    if (shouldShow) {
      sticky.classList.add('is-active');
      this.syncStickyColumnWidths();
      this.updateStickyHorizontal();
    } else {
      sticky.classList.remove('is-active');
    }
  }

  private syncStickyColumnWidths(): void {
    const container = this.tableContainer?.nativeElement;
    const sticky = this.stickyHeader?.nativeElement;

    if (!container || !sticky) {
      return;
    }

    const sourceHeader = container.querySelector('thead');
    const cloneHeader = sticky.querySelector('thead');

    if (!sourceHeader || !cloneHeader) {
      return;
    }

    const cloneTable = sticky.querySelector('table');
    const sourceRow = sourceHeader.querySelector('tr:first-child');

    if (cloneTable && sourceRow) {
      const rowCells = Array.from(sourceRow.querySelectorAll('th')) as HTMLElement[];

      if (rowCells.length > 0) {
        const colgroup = document.createElement('colgroup');

        for (const cell of rowCells) {
          const col = document.createElement('col');
          const width = cell.getBoundingClientRect().width;
          col.style.width = `${width}px`;
          col.style.minWidth = `${width}px`;
          col.style.maxWidth = `${width}px`;
          colgroup.appendChild(col);
        }

        const existingColGroups = Array.from(cloneTable.querySelectorAll('colgroup'));
        existingColGroups.forEach((group) => group.remove());

        const insertBeforeNode = cloneTable.firstChild;
        cloneTable.insertBefore(colgroup, insertBeforeNode);
      }
    }

    const sourceCells = Array.from(sourceHeader.querySelectorAll('th'));
    const cloneCells = Array.from(cloneHeader.querySelectorAll('th'));
    const count = Math.min(sourceCells.length, cloneCells.length);

    for (let i = 0; i < count; i += 1) {
      const sourceCell = sourceCells[i];
      const cloneCell = cloneCells[i] as HTMLElement;
      const sourceWidth = sourceCell.getBoundingClientRect().width;

      cloneCell.style.setProperty('width', `${sourceWidth}px`, 'important');
      cloneCell.style.setProperty('min-width', `${sourceWidth}px`, 'important');
      cloneCell.style.setProperty('max-width', `${sourceWidth}px`, 'important');
    }
  }

  private getScrollParent(): HTMLElement | Window {
    let parent = this.elementRef.nativeElement as HTMLElement | null;

    while (parent) {
      parent = parent.parentElement;
      if (!parent) {
        break;
      }

      const style = getComputedStyle(parent);
      if (/(auto|scroll)/.test(style.overflowY)) {
        return parent;
      }
    }

    return window;
  }

  getColumnWidth(
    alternativesCount: number,
    alternatives?: { title: string }[],
    questionId?: string,
  ): string {
    return MatrixColumnWidthUtil.getColumnWidth(
      alternativesCount,
      alternatives,
      questionId,
      'matrix',
    );
  }

  getColumnMinWidth(title: string, questionId?: string): string {
    return MatrixColumnWidthUtil.getColumnMinWidth(title, questionId);
  }
}
